/**
 * PmsShellV4 — the single PMS shell (SCRUM-297 / MENU-1, epic SCRUM-296).
 *
 * Replaces `DashboardPage`'s shell role at the authenticated root (`/`):
 * one flex-row frame wrapping every PMS page —
 *
 *     [ SidebarV4 ] + column[ TopBarV4 + scrolling <main> ]
 *
 * `<main>` is the ONLY scroll region; the routed child renders via
 * `<Outlet/>` inside it. The home content (GreetingHero + WidgetGrid) is the
 * `path: ''` index child (`DashboardHome`) — this shell carries no home
 * content itself, so there is no double chrome.
 *
 * **Carries DashboardPage's shipped behavior forward (no regression):**
 *  - Account: the real shared `AccountMenu` (entities/account) wired
 *    identically — sign-out (POST /auth/logout → queryClient.clear →
 *    navigateToLogin), density, theme, `me.portals` switcher, action links,
 *    Escape-close, `triggerRef`.
 *  - Theme: `useApplyThemePreference(preferences.theme)` applies the
 *    persisted theme on mount + change (the shared applier; replaces
 *    DashboardPage's local `applyTheme` effect). `onTheme` ALSO applies
 *    imperatively so the swap is one-frame on click (matches the shipped
 *    instant-swap contract).
 *  - Compact / mobile: `useCompactMode(compactOverride)`; on compact the
 *    sidebar rail is hidden and a hamburger in `TopBarV4` opens the shared
 *    `MobileNavDrawer` (entities/navigation) fed from `NAV_V4` (flat
 *    routable destinations). Desktop ≥900 → `SidebarV4` rail.
 *  - Render guard: `user === null` / `orgState === null` → render nothing
 *    (the parent `AuthenticatedRootPage` owns the loading/error/auth
 *    branches), carried verbatim from DashboardPage.
 *
 * Search / quick-add / alerts in the top bar are skeleton (TODO(SCRUM-298/299));
 * full Sidebar/TopBar behavior lands in MENU-2/3.
 */
import * as React from 'react';
import * as Sentry from '@sentry/react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { apiClient } from '../../shared/api-client.js';
import { queryClient } from '../../shared/query-client.js';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser.js';
import { useMe } from '../../shared/auth/use-me.js';
import { useCurrentOrg } from '../../shared/hooks/useCurrentOrg.js';
import { useUserPreferences } from '../../shared/hooks/useUserPreferences.js';
import { useCompactMode } from '../../shared/hooks/useCompactMode.js';
import { useApplyThemePreference } from '../../shared/hooks/useApplyThemePreference.js';
import { useHiddenNav } from '../../shared/hooks/useHiddenNav.js';

import {
  AccountMenu,
  type AccountMenuActionLink,
} from '../../entities/account/AccountMenu.js';
import { MobileNavDrawer } from '../../entities/navigation/MobileNavDrawer.js';
// SidebarV4 graduated to the shared entities tier in MENU-2 (SCRUM-298) — same
// deep-import style as MobileNavDrawer above (no parent barrel).
import { SidebarV4 } from '../../entities/navigation/sidebar-v4/index.js';

// TopBarV4 graduated to the shared entities tier in MENU-3 (SCRUM-299) — same
// deep-import style as MobileNavDrawer / SidebarV4 above (no parent barrel).
import {
  TopBarV4,
  type QuickAddItem,
} from '../../entities/navigation/top-bar-v4/index.js';
import {
  NAV_V4,
  NAV_V4_ROUTABLE,
  navToMobileItems,
  pathnameToNavV4Active,
} from './nav-model.js';
import { navigateToLogin } from './navigate.js';

export interface PmsShellV4Props {
  /** Override compact mode for tests (otherwise auto-detected). */
  readonly compact?: boolean;
}

/** PMS account-menu action links (placeholder routes under `/`, same as the
    shipped DashboardPage set so the menu's links resolve in-portal). */
const PMS_ACTION_LINKS: readonly AccountMenuActionLink[] = [
  { label: 'Account settings', icon: 'user', to: '/account' },
  { label: 'Notifications', icon: 'bell', to: '/notifications' },
  { label: 'Help & support', icon: 'info', to: '/help' },
];

/**
 * The single `localStorage` key for the hidden-nav set (MENU-4 / SCRUM-300).
 * The SAME key is passed to BOTH `useHiddenNav` (to filter the mobile drawer)
 * AND `<SidebarV4 storageKey>` (its edit-menu), so the desktop rail + the
 * compact drawer share one hidden set via the `useHiddenNav` store. Never type
 * the literal in two places.
 */
const PMS_HIDDEN_NAV_KEY = 'desata.v4.hiddenNav';

/**
 * PMS quick-add entities (the 5 creatable types from the design + COPY.md).
 * Static config — NOT fabricated API data — so it is honest to ship now; each
 * fires `onQuickAdd(label)` and the create flow it opens is wired in MENU-6
 * (SCRUM-302). Module-level (stable).
 */
const PMS_QUICK_ADD: readonly QuickAddItem[] = [
  { icon: 'receipt', label: 'Reservation' },
  { icon: 'checks', label: 'Task' },
  { icon: 'chart', label: 'Expense' },
  { icon: 'user', label: 'User' },
  { icon: 'building', label: 'Property' },
];

/**
 * Apply a theme imperatively so the `<html data-theme>` swap is one-frame on
 * a toggle click (the shared `useApplyThemePreference` reacts to preference
 * *changes*, but the persisted write round-trips through the server query —
 * applying here too matches DashboardPage's shipped instant-swap).
 */
function applyTheme(theme: 'light' | 'dark' | 'system'): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'system') {
    root.removeAttribute('data-theme');
    return;
  }
  root.setAttribute('data-theme', theme);
}

export function PmsShellV4({
  compact: compactOverride,
}: PmsShellV4Props = {}): React.ReactElement | null {
  const user = useCurrentUser();
  // Portal entitlements for the account menu's shared "Switch portal"
  // section. useCurrentUser exposes only { id, email }; useMe adds portals[].
  const me = useMe();
  const orgState = useCurrentOrg();
  const { preferences, setTheme, setDensity } = useUserPreferences();
  const compact = useCompactMode(compactOverride);

  const location = useLocation();
  const navigate = useNavigate();
  const active = pathnameToNavV4Active(location.pathname);

  // Hidden-nav set (MENU-4) — shared with SidebarV4's edit-menu via the SAME
  // key. Filter the TOP-LEVEL model BEFORE flattening so a hidden group (e.g.
  // `operations`) also drops its flattened routable child (`ops-users → /users`);
  // filtering the already-flat list by top-level ids would leak that child.
  const { hidden } = useHiddenNav(PMS_HIDDEN_NAV_KEY);
  const mobileNavItems = React.useMemo(
    () => navToMobileItems(NAV_V4.filter((node) => !hidden.has(node.id))),
    [hidden],
  );

  const [accountMenuOpen, setAccountMenuOpen] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  // Desktop rail collapse (64px icon rail ↔ 248px). Session-local for now;
  // the Edit-menu hide/restore + localStorage persistence is MENU-4 (SCRUM-300).
  const [collapsed, setCollapsed] = React.useState(false);
  // Shared trigger ref so AccountMenu's outside-click handler can exclude the
  // avatar trigger from its close-on-mousedown set (SCRUM-15 reviewer fix #4).
  const accountMenuTriggerRef = React.useRef<HTMLButtonElement>(null);

  // Apply the persisted theme on mount + whenever it changes (shared applier;
  // single source replacing DashboardPage's local effect).
  useApplyThemePreference(preferences.theme);

  // Escape closes the account menu.
  React.useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setAccountMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [accountMenuOpen]);

  // Render guard carried verbatim from DashboardPage — the parent
  // AuthenticatedRootPage owns the loading / unauthenticated / error branches.
  if (user === null) return null;
  if (orgState === null) return null;

  const onTheme = (t: 'light' | 'dark' | 'system'): void => {
    setTheme(t);
    applyTheme(t);
  };

  const onDensity = (m: 'lite' | 'pro'): void => {
    setDensity(m);
  };

  /**
   * Sign-out flow — identical to the shipped DashboardPage path:
   *  1. POST /auth/logout (best-effort server revoke; failures are reported
   *     to Sentry but must not strand the user).
   *  2. queryClient.clear() evicts user-bound cache (CLAUDE.md FE rule §10).
   *  3. Hard navigate to /login so the React tree boots fresh.
   */
  const onSignOut = async (): Promise<void> => {
    try {
      await apiClient.POST('/auth/logout', {});
    } catch (error) {
      Sentry.captureException(error, {
        tags: { surface: 'pms-shell-logout', endpoint: 'POST /auth/logout' },
      });
    }
    queryClient.clear();
    navigateToLogin();
  };

  const onSelect = (id: string): void => {
    const path = NAV_V4_ROUTABLE[id];
    if (path !== undefined) navigate(path);
    // Not-yet-routed destinations no-op. Non-routable rows now read DISABLED in
    // BOTH surfaces — the mobile drawer and (via `isRoutable` below) the desktop
    // sidebar — so the only id that still reaches this no-op branch is Agency:
    // it stays interactive (it expands its agent quick-list) yet has no route
    // until MENU-6 (SCRUM-302), which wires the remaining leaf routes.
  };

  // Agent quick-chat handler — documented stub. MENU-5 (SCRUM-301) ships the
  // HONEST EMPTY placeholder: PMS passes `agents={[]}` (there is no agents
  // backend yet — see the BE-need), so the expanded Agency shows "No agents
  // connected yet" and this handler is currently unreachable. Real agents +
  // chat arrive when the agents/chat backend lands (revisits MENU-5 / an AI epic).
  const onAgentChat = (_agentId: string): void => {
    // TODO(SCRUM-302+): open the agent chat panel for `_agentId` once the
    // agents/chat backend exists.
  };

  // Top-bar actions (Athena, quick-add create flows) are wired by the host in
  // MENU-6 (SCRUM-302). Until then PMS passes NEITHER `onAI` nor `onQuickAdd`,
  // so TopBarV4 renders those controls DISABLED — an honest "not wired yet"
  // (matching the disabled Alerts/Help), never an enabled control that silently
  // no-ops (Simon review, #305). No fabricated data either: `searchResults` +
  // `unreadCount` stay omitted (honest empty / zero). `quickAddItems` is static
  // product config (the 5 creatable types), so it's honest to pass now; the
  // disabled trigger just doesn't open until MENU-6 supplies `onQuickAdd`.

  return (
    <div className="h-screen flex bg-[var(--bg-canvas)] text-[var(--fg-primary)]">
      {/* Desktop rail — hidden on compact (replaced by the hamburger+drawer). */}
      {!compact ? (
        <SidebarV4
          active={active}
          onSelect={onSelect}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((c) => !c)}
          onAgentChat={onAgentChat}
          // Explicitly empty — no agents backend yet (MENU-5 / SCRUM-301). The
          // sidebar shows an honest "No agents connected yet" line, NOT mock
          // rows. Real agents arrive when the backend lands (BE-need filed).
          agents={[]}
          navModel={NAV_V4}
          // Desktop sidebar carries the SAME routability/disabled contract as
          // the mobile drawer: a destination row whose id is not in
          // NAV_V4_ROUTABLE renders disabled rather than a dead button (Simon
          // P2, SCRUM-298). The remaining real routes land in MENU-6 (SCRUM-302).
          isRoutable={(id) =>
            Object.prototype.hasOwnProperty.call(NAV_V4_ROUTABLE, id)
          }
          brand="Desata"
          storageKey={PMS_HIDDEN_NAV_KEY}
        />
      ) : null}

      {/* Column: top bar + the single scrolling <main>. `min-w-0` lets the
          column shrink so long content / wide tables don't blow out the row. */}
      <div className="flex-1 min-w-0 flex flex-col h-full">
        <TopBarV4
          user={user}
          compact={compact}
          onMenu={() => setDrawerOpen(true)}
          onAccount={() => setAccountMenuOpen((v) => !v)}
          accountOpen={accountMenuOpen}
          accountTriggerRef={accountMenuTriggerRef}
          quickAddItems={PMS_QUICK_ADD}
          accountChildren={
            <AccountMenu
              user={user}
              density={preferences.density}
              theme={preferences.theme}
              onDensityChange={onDensity}
              onThemeChange={onTheme}
              portals={me?.portals ?? []}
              currentPortal="PMS"
              actionLinks={PMS_ACTION_LINKS}
              onClose={() => setAccountMenuOpen(false)}
              onSignOut={onSignOut}
              triggerRef={accountMenuTriggerRef}
            />
          }
        />

        {/* The ONLY scroll region. The routed child (DashboardHome at `/`,
            or any nested PMS page) renders here via <Outlet/>. */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* Compact: shared off-canvas drawer fed from NAV_V4 (flat routable
          destinations), MINUS any items hidden via the edit-menu (MENU-4) —
          same hidden set as the desktop rail (filtered before flattening). */}
      {compact ? (
        <MobileNavDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          items={mobileNavItems}
          activeId={active}
          user={user}
        />
      ) : null}
    </div>
  );
}
